import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import MenuItem from '@material-ui/core/MenuItem'
import MuiTextField from '@material-ui/core/TextField'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'

import PageTitle from '../../components/PageTitle'
import Widget from '../../components/Widget'
import { Button, Typography } from '../../components/Wrappers'
import { useAppointments, useAppointmentHandlers, useAppointmentTypes, useBusinessHours } from '../../apis'
import AppointmentCalendar from './AppointmentCalendar'
import AppointmentForm from './AppointmentForm'
import useStyles from './styles'
import {
    VIEW_DAY,
    VIEW_MONTH,
    VIEW_WEEK,
    clampDate,
    formatHandlerName,
    navigationBounds,
    toDateOnly,
    toLocalDate,
    visibleRange,
} from './calendarHelpers'

/**
 * Appointments - the appointments list page (Requirements 10.1 - 10.6). Open to
 * EVERY authenticated user: nothing here reads a role.
 *
 * ROUTING (task 12.3). ONE component serves BOTH routes, and the URL is the
 * source of truth for "which appointment is open":
 *
 *   <Route path="/app/appointments/:id" component={Appointments} />
 *   <Route path="/app/appointments" component={Appointments} />
 *
 * THE ':id' ROUTE MUST BE LISTED FIRST inside the <Switch> (the same way
 * /app/household/:householdId/householdCase/:caseId precedes /app/household/:householdId
 * in Layout.js). Neither path is `exact`, so if the bare path came first it would
 * also match /app/appointments/5 and `useParams()` would hand this component no
 * id - a deep link would then silently open no panel.
 *
 * WHY THE URL AND NOT A DIALOG: /app/appointments/:id has to work as a deep link
 * (Requirement 10.5), which means the open appointment must be derivable from the
 * route on a cold load. Clicking a block therefore pushes the route and the panel
 * follows, so Back closes it and the link is shareable. The shared dialog from
 * DialogContext would need the same route handling on top of its own state, and
 * its md width has no room for the form's handler-week sidebar.
 *
 * CREATING is the one thing NOT in the URL: task 12.3 only adds the two routes
 * above, so a '/app/appointments/new' path would arrive here as id='new'. The
 * "New Appointment" button in the page title opens the same panel in create mode
 * from local state instead (Requirement 1.1's entry point), and only numeric ids
 * are treated as an appointment to edit.
 *
 * THE RANGE IS ALWAYS EXPLICIT: GET /appointments has NO default date range -
 * absent from/to means UNBOUNDED - so every fetch sends the from/to of the range
 * on screen, taken from visibleRange(view, anchor). Requirement 10.4's "current
 * week by default" is therefore just the default view (week) and the default
 * anchor (today).
 */
const Appointments = () => {
    const classes = useStyles()
    const history = useHistory()

    // Only a numeric :id is an appointment. Anything else (a stray path segment)
    // is ignored rather than fetched as /appointments/whatever.
    const { id: routeId } = useParams()
    const openAppointmentId = /^\d+$/.test(String(routeId || '')) ? routeId : null

    //Requirement 10.2 - week is the default, day and month are a toggle away
    const [view, setView] = useState(VIEW_WEEK)
    //Requirement 10.4 - the default anchor is today, i.e. the current week
    const [anchor, setAnchor] = useState(() => toDateOnly(new Date()))

    //Requirement 10.4 - '' means "no constraint": useAppointments DROPS blank
    //params, so "All handlers" sends nothing rather than a sentinel value
    const [handler, setHandler] = useState('')
    const [appointmentType, setAppointmentType] = useState('')
    //the optional date range, as 'YYYY-MM-DD' strings or null when cleared
    const [rangeFrom, setRangeFrom] = useState(null)
    const [rangeTo, setRangeTo] = useState(null)

    //create mode is local state; edit mode comes from the route (see the header)
    const [isCreating, setCreating] = useState(false)

    const bounds = useMemo(() => navigationBounds(), [])
    const range = useMemo(() => visibleRange(view, anchor), [view, anchor])

    // The fetched range is the range ON SCREEN, narrowed by the date range filter
    // when the user set one. Narrowing rather than replacing keeps the two
    // consistent: the calendar can only render the days it is showing, so asking
    // for anything outside them would return rows with nowhere to go.
    // `from` > `to` is a legal empty range server side, which is what an
    // out-of-view filter collapses to (and the notice below explains it).
    const queryFrom = rangeFrom && rangeFrom > range.start ? rangeFrom : range.start
    const queryTo = rangeTo && rangeTo < range.end ? rangeTo : range.end
    const rangeExcludesView = !!queryFrom && !!queryTo && queryFrom > queryTo

    const { appointments, isLoading, isError, mutate } = useAppointments({
        handler,
        appointmentType,
        from: queryFrom,
        to: queryTo,
    })

    const { handlers, isError: handlersError, mutate: mutateHandlers } = useAppointmentHandlers()
    const {
        appointmentTypes,
        isError: typesError,
        mutate: mutateTypes,
    } = useAppointmentTypes()
    const {
        businessHours,
        isLoading: businessHoursLoading,
        isError: businessHoursError,
    } = useBusinessHours()

    const panelMode = openAppointmentId ? 'edit' : isCreating ? 'create' : null

    // Focus handling for the panel (Requirement 10.5, keyboard): focus moves into
    // the region when it opens and goes back where it came from when it closes.
    const panelRef = useRef(null)
    const returnFocusRef = useRef(null)

    useEffect(() => {
        if (panelMode && panelRef.current) panelRef.current.focus()
    }, [panelMode, openAppointmentId])

    const rememberFocus = () => {
        returnFocusRef.current = document.activeElement
    }

    // The clicked block is often gone by the time the panel closes (the list was
    // revalidated), so focus falls back to the New Appointment button instead of
    // being dropped on <body>. The button is found by id because the Wrappers
    // Button is a plain function component and cannot take a ref.
    const restoreFocus = () => {
        const previous = returnFocusRef.current
        returnFocusRef.current = null
        if (previous && previous.focus && document.body.contains(previous)) {
            previous.focus()
            return
        }
        const newButton = document.getElementById('appointments-new-appointment')
        if (newButton) newButton.focus()
    }

    const closePanel = () => {
        setCreating(false)
        //back to the bare route, so the closed panel is not reopened by a reload
        if (openAppointmentId) history.push('/app/appointments')
        restoreFocus()
    }

    // Everything the panel reports lands here: 'create', 'edit' AND 'cancel'
    // (AppointmentForm reports a cancellation through the same onSaved, with mode
    // 'cancel'). All three need the same two things - refetch the range on screen
    // and close the panel - so the mode is not branched on.
    //
    // INCLUDING CANCEL MATTERS: GET /appointments EXCLUDES cancelled appointments,
    // so the block vanishes from the calendar on this refetch. Leaving the panel
    // open would leave the user editing a record the view behind it no longer
    // contains; it closes instead, and the cancellation snackbar says what
    // happened. The appointment is still on the household's page, as cancelled.
    const handleSaved = () => {
        mutate()
        closePanel()
    }

    //Requirement 10.5 - clicking an appointment opens its full record
    const openAppointment = appointment => {
        if (!appointment || !appointment.id) return
        rememberFocus()
        setCreating(false)
        history.push(`/app/appointments/${appointment.id}`)
    }

    const openCreate = () => {
        rememberFocus()
        if (openAppointmentId) history.push('/app/appointments')
        setCreating(true)
    }

    // Dismissible from the keyboard. Escape raised inside a text entry is left
    // alone: those controls own the key (Autocomplete clears, a picker closes),
    // and closing the panel out from under somebody mid-edit would throw away
    // what they typed. From the region itself, a button or a select, Escape closes.
    const handlePanelKeyDown = event => {
        if (event.key !== 'Escape') return
        const target = event.target
        const tagName = target && target.tagName ? target.tagName.toLowerCase() : ''
        if (tagName === 'input' || tagName === 'textarea') return
        event.stopPropagation()
        closePanel()
    }

    const changeView = (event, nextView) => {
        //an exclusive ToggleButtonGroup reports null when the active button is
        //clicked again; keeping the current view is better than having none
        if (nextView) setView(nextView)
    }

    const changeRangeFrom = value => {
        const dateOnly = toDateOnly(value)
        setRangeFrom(dateOnly)
        // Keep the filter and the calendar in step: picking a From date SHOWS
        // that date's range instead of leaving the user on a week the filter has
        // just emptied. Clamped, because the calendar cannot navigate outside the
        // Requirement 9.4 window.
        if (dateOnly) setAnchor(clampDate(dateOnly, bounds))
    }

    const clearFilters = () => {
        setHandler('')
        setAppointmentType('')
        setRangeFrom(null)
        setRangeTo(null)
    }

    const retryLookups = () => {
        if (handlersError && mutateHandlers) mutateHandlers()
        if (typesError && mutateTypes) mutateTypes()
    }

    const unit = view === VIEW_DAY ? 'day' : view === VIEW_MONTH ? 'month' : 'week'
    const hasFilters = !!handler || !!appointmentType || !!rangeFrom || !!rangeTo

    // Requirement 10.6 - the empty state says WHY it is empty, so a filtered-out
    // calendar is not read as "nothing is scheduled".
    const emptyMessage = hasFilters
        ? `No appointments match the current filters for this ${unit}.`
        : `No appointments are scheduled for this ${unit}.`

    //named so a failed lookup says WHICH filter list is unavailable
    const failedLookups = [handlersError ? 'handlers' : null, typesError ? 'appointment types' : null].filter(Boolean)

    //create-mode prefills: the day the user is looking at, but never a past date
    //(Requirement 1.6 - the form's own floor), and the handler already filtered on
    const createDate = anchor && anchor >= bounds.today ? anchor : bounds.today

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <PageTitle
                title="Appointments"
                button={
                    /* Requirement 1.1's entry point - a list page needs a way to add */
                    <Button
                        id="appointments-new-appointment"
                        variant="contained"
                        color="primary"
                        onClick={openCreate}
                    >
                        New Appointment
                    </Button>
                }
            />

            {/* Requirement 10.5 - the detail / edit panel. A labelled region rather
                than a modal: it takes focus when it opens, Escape closes it, and the
                calendar stays visible underneath. */}
            {!!panelMode && (
                <div
                    ref={panelRef}
                    tabIndex={-1}
                    role="region"
                    aria-labelledby="appointments-panel-heading"
                    onKeyDown={handlePanelKeyDown}
                    className={classes.detailPanel}
                >
                    <Widget
                        disableWidgetMenu
                        header={
                            <div className={classes.detailPanelHeader}>
                                <Typography
                                    id="appointments-panel-heading"
                                    variant="h5"
                                    color="text"
                                    colorBrightness="secondary"
                                >
                                    {panelMode === 'edit' ? 'Appointment' : 'New Appointment'}
                                </Typography>
                                <Typography variant="caption" color="text" colorBrightness="secondary">
                                    Press Escape to close
                                </Typography>
                            </div>
                        }
                    >
                        {/* Keyed on what is open, so switching appointments rebuilds the
                            form instead of reusing another record's field state. The form
                            owns cancelling (Requirements 13.2 / 13.5) and reports it
                            through onSaved with mode 'cancel' - see handleSaved. */}
                        <AppointmentForm
                            key={panelMode === 'edit' ? `edit-${openAppointmentId}` : 'create'}
                            appointmentId={panelMode === 'edit' ? openAppointmentId : undefined}
                            date={panelMode === 'create' ? createDate : undefined}
                            handlerId={panelMode === 'create' && handler ? handler : undefined}
                            onSaved={handleSaved}
                            onClose={closePanel}
                        />
                    </Widget>
                </div>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    {/* Requirements 10.2 / 10.4 - the view toggle and the filters */}
                    <Widget
                        disableWidgetMenu
                        header={
                            <div className={classes.filterHeader}>
                                <Typography variant="h5" color="text" colorBrightness="secondary">
                                    View & Filters
                                </Typography>
                                <div className={classes.filterViewToggle}>
                                    <Typography
                                        id="appointments-view-label"
                                        variant="caption"
                                        color="text"
                                        colorBrightness="secondary"
                                        className={classes.filterGroupLabel}
                                    >
                                        Calendar view
                                    </Typography>
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={view}
                                        onChange={changeView}
                                        role="group"
                                        aria-labelledby="appointments-view-label"
                                    >
                                        <ToggleButton value={VIEW_DAY} aria-label="Day view">
                                            Day
                                        </ToggleButton>
                                        <ToggleButton value={VIEW_WEEK} aria-label="Week view">
                                            Week
                                        </ToggleButton>
                                        <ToggleButton value={VIEW_MONTH} aria-label="Month view">
                                            Month
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </div>
                            </div>
                        }
                    >
                        <div className={classes.filterBar}>

                            {/* '' is the "no constraint" option: the hook drops blank
                                params, so All sends no `handler` at all */}
                            <MuiTextField
                                select
                                id="appointments-filter-handler"
                                label="Handler"
                                value={handler}
                                onChange={event => setHandler(event.target.value)}
                                variant="outlined"
                                margin="normal"
                                size="small"
                                fullWidth
                                className={classes.filterField}
                            >
                                <MenuItem value="">All handlers</MenuItem>
                                {handlers.map(option => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {formatHandlerName(option)}
                                    </MenuItem>
                                ))}
                            </MuiTextField>

                            <MuiTextField
                                select
                                id="appointments-filter-type"
                                label="Appointment Type"
                                value={appointmentType}
                                onChange={event => setAppointmentType(event.target.value)}
                                variant="outlined"
                                margin="normal"
                                size="small"
                                fullWidth
                                className={classes.filterField}
                            >
                                <MenuItem value="">All types</MenuItem>
                                {appointmentTypes.map(option => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </MuiTextField>

                            {/* the range narrows what is fetched for the days on screen;
                                setting From also moves the calendar to that date */}
                            <KeyboardDatePicker
                                id="appointments-filter-from"
                                label="From date"
                                value={rangeFrom ? toLocalDate(rangeFrom) : null}
                                onChange={changeRangeFrom}
                                clearable
                                format="MM/dd/yyyy"
                                mask="__/__/____"
                                minDate={toLocalDate(bounds.minDate)}
                                maxDate={toLocalDate(bounds.maxDate)}
                                inputVariant="outlined"
                                margin="normal"
                                size="small"
                                fullWidth
                                className={classes.filterField}
                            />
                            <KeyboardDatePicker
                                id="appointments-filter-to"
                                label="To date"
                                value={rangeTo ? toLocalDate(rangeTo) : null}
                                onChange={value => setRangeTo(toDateOnly(value))}
                                clearable
                                format="MM/dd/yyyy"
                                mask="__/__/____"
                                minDate={toLocalDate(bounds.minDate)}
                                maxDate={toLocalDate(bounds.maxDate)}
                                inputVariant="outlined"
                                margin="normal"
                                size="small"
                                fullWidth
                                className={classes.filterField}
                            />

                            <div className={classes.filterActions}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={clearFilters}
                                    disabled={!hasFilters}
                                >
                                    Clear Filters
                                </Button>
                            </div>

                            {/* an empty calendar caused by the filter itself is explained
                                rather than left looking like an empty schedule */}
                            {!!rangeExcludesView && (
                                <div className={classes.filterNotice}>
                                    <Typography variant="body2" color="secondary">
                                        {`The date range filter does not overlap the ${unit} on screen, so nothing can be shown. Navigate the calendar into the range or clear the filter.`}
                                    </Typography>
                                </div>
                            )}

                            {/* the selects can only offer what loaded, so a failed lookup
                                is said out loud instead of looking like an empty list */}
                            {!!failedLookups.length && (
                                <div className={classes.filterNotice} role="alert">
                                    <Typography variant="body2" color="secondary">
                                        {`These filter options could not be loaded: ${failedLookups.join(', ')}.`}
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        onClick={retryLookups}
                                        className={classes.margin}
                                    >
                                        Retry
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Widget>
                </Grid>

                <Grid item xs={12}>
                    <Widget title="Appointment Calendar" disableWidgetMenu>
                        {/* Requirements 10.2 / 10.3 / 10.5 / 10.6 - the shared calendar.
                            `showHandler` adds the handler name to each block, which the
                            list endpoint (and only the list endpoint) provides. */}
                        <AppointmentCalendar
                            appointments={appointments}
                            view={view}
                            anchorDate={anchor}
                            onAnchorDateChange={setAnchor}
                            onAppointmentClick={openAppointment}
                            showHandler
                            isLoading={isLoading}
                            isError={isError}
                            onRetry={mutate}
                            emptyMessage={emptyMessage}
                            bounds={bounds}
                            businessHours={businessHours}
                            businessHoursLoading={businessHoursLoading}
                            businessHoursError={businessHoursError}
                        />
                    </Widget>
                </Grid>
            </Grid>
        </MuiPickersUtilsProvider>
    )
}

export default Appointments
